import React, { useState, useEffect, useRef } from 'react';
import TranslationLoadingScreen from './TranslationLoadingScreen';
import TranslationPreview from './TranslationPreview';
import translationAPI from '../services/translationAPI';
import { uploadAPI, attractionsAPI } from '../services/api';
import api from '../services/api'; // Static import for axios instance
import OperatingHours from './OperatingHours';
import { getLanguageDisplay, buildLanguageOptions, buildLanguageMap, formatLanguageList } from '../utils/languageConfig';
// Removed pricing labels service to avoid errors
import './EnhancedAttractionForm.css';

const EnhancedAttractionForm = ({
  onClose,
  onSave,
  editingAttraction = null,
  isOpen = false,
  halalTracking = false
}) => {
  // Form ref for direct access to form data
  const formRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    display_category: '',
    description: '',
    address: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    district: '',
    latitude: '',
    longitude: '',
    price_adult: '',
    price_child: '',
    price_senior_citizen: '',
    price_tourist: '',
    currency: 'USD',
    pricing_type: 'flat_rate',
    operating_hours: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    },
    tips: '',
    history: '',
    website_url: '',
    social_links: {},
    is_bookable_online: false,
    contact_number: '',
    map_url: '',
    media_links: [],
    pic_contact: '',
    halal_status: '',
    keywords: '',
    star_rating: '',
    orkid_rating: '',
  });

  // Stampbook slot info (read-only, fetched from backend)
  const [stampbookSlotInfo, setStampbookSlotInfo] = useState(null);

  // Translation workflow state
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState({
    step: 'initializing',
    progress: 0,
    currentLanguage: '',
    completedLanguages: 0,
    error: null
  });
  const [translatedData, setTranslatedData] = useState(null);
  const [showTranslationPreview, setShowTranslationPreview] = useState(false);
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [viewingLanguage, setViewingLanguage] = useState('en');

  // Tenant config — fetched on mount; drives language lists, feature flags
  const [tenantConfig, setTenantConfig] = useState(null);
  
  // Image upload state
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [uploadedImages, setUploadedImages] = useState([]);
  
  // Removed pricing labels service to avoid errors
  
  // Auto-detect and force correct source language (returns ISO code)
  const getCorrectSourceLanguage = (data) => {
    const text = (data.description || data.name || '').toLowerCase();

    // Check for English indicators
    if (text.includes('company') || text.includes('registered') || text.includes('established') ||
        text.includes('the ') || text.includes(' is ') || text.includes(' was ')) {
      return 'en';
    }

    // Check for Malay indicators
    if (text.includes('syarikat') || text.includes('didaftarkan') || text.includes('adalah') ||
        text.includes('ialah') || text.includes('yang')) {
      return 'ms';
    }

    // Check for Finnish indicators
    if (text.includes('sijaitsee') || text.includes('alueella') || text.includes('vuonna') ||
        text.includes('suomen') || text.includes('järvi')) {
      return 'fi';
    }

    // Default to English
    return 'en';
  };

  // Removed pricing labels to avoid errors


  const [translationError, setTranslationError] = useState('');
  const [quickSaveError, setQuickSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Form validation
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef(null);

  // Dynamic categories fetched from tenant DB (mirrors Attractions.js pattern)
  const [categories, setCategories] = useState([]);
  // When true, the Category control swaps the dropdown for a free-text input so
  // admins can create a brand-new category slug (the dropdown only lists slugs
  // that already exist in the DB). See handleCategorySelectChange.
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await attractionsAPI.getFilterOptions();
        const cats = response.data?.categories || [];
        setCategories(cats);
      } catch (error) {
        console.error('Failed to fetch category options:', error);
      }
    };
    if (isOpen) fetchCategories();
  }, [isOpen]);

  // Fetch tenant config (languages + features) on mount
  useEffect(() => {
    const fetchTenantConfig = async () => {
      try {
        const response = await api.get('/api/v1/config');
        if (response.data?.success) {
          setTenantConfig(response.data.data);
        }
      } catch (error) {
        console.warn('Failed to fetch tenant config:', error.message);
      }
    };
    if (isOpen) fetchTenantConfig();
  }, [isOpen]);

  // Static subcategory mapping — only applies to tenants whose categories match.
  // Unknown categories gracefully return an empty array (no subcategory options).
  const categorySubcategories = {
    'Attraction': ['Culture', 'History', 'Education', 'Health'],
    'Restaurant': ['Dining'],
    'Accommodation': ['Hotel', 'Resort', 'Chalet', 'Apartment / Townstay / Guesthouse', 'Homestay', 'Convention'],
    'Shopping': ['Retail'],
    'Entertainment': ['Recreation'],
    'Natural Wonder': ['Ecotourism', 'Agriculture'],
    'Adventure': ['Youth', 'Sports'],
    'Religious Site': ['Religion']
  };

  // Get available subcategories based on selected category
  const getAvailableSubcategories = (category) => {
    return categorySubcategories[category] || [];
  };

  const currencies = ['USD', 'MYR', 'EUR', 'SGD', 'GBP', 'JPY', 'CNY'];

  const pricingTypes = [
    { value: 'flat_rate', label: 'Flat Rate' },
    { value: 'tiered', label: 'Tiered Pricing' },
    { value: 'free', label: 'Free Entry' },
    { value: 'varies', label: 'Price Varies' }
  ];

  // Helper function to transform object to array for editing
  // eslint-disable-next-line no-unused-vars
  const transformObjectToArray = (obj) => {
    if (!obj) return [];
    if (Array.isArray(obj)) return obj;
    if (typeof obj === 'object') {
      return Object.keys(obj).filter(key => obj[key] === true);
    }
    return [];
  };

  // Helper function to convert operating hours from old format (text) to new format (JSON)
  const parseOperatingHours = (operatingHours) => {
    // Default structure
    const defaultHours = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    };

    // If empty or null, return default
    if (!operatingHours || (typeof operatingHours === 'string' && operatingHours.trim() === '')) {
      return defaultHours;
    }

    // If it's a string, try to parse it as JSON (could be JSON string from API)
    if (typeof operatingHours === 'string') {
      try {
        const parsed = JSON.parse(operatingHours);
        // If parsed successfully and has the expected structure
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.monday !== undefined) {
          console.log('✅ Parsed operating hours from JSON string');
          return parsed;
        }
      } catch (e) {
        // Not valid JSON, might be old text format
        console.log('⚠️ Operating hours in old text format, using default structure');
        return defaultHours;
      }
    }

    // If already in new JSON format (object), return it
    if (operatingHours && typeof operatingHours === 'object' && !Array.isArray(operatingHours)) {
      // Validate structure
      if (operatingHours.monday !== undefined) {
        console.log('✅ Operating hours already in JSON format');
        return operatingHours;
      }
    }

    return defaultHours;
  };



  // Config-driven language list (ISO-keyed)
  const supportedCodes = tenantConfig?.supportedLanguages || ['en'];
  const languages = buildLanguageOptions(supportedCodes);
  const languageMap = buildLanguageMap(supportedCodes);

  // Function to get field value for current viewing language (ISO-keyed)
  const getFieldValue = (fieldName, attraction = editingAttraction, forceLanguage = null) => {
    if (!attraction) return '';

    const targetLanguage = forceLanguage || viewingLanguage;

    let value = '';
    if (targetLanguage === 'en') {
      // English: read from base column (tenant path) or _english column (legacy)
      value = attraction[fieldName] || attraction[`${fieldName}_english`] || '';
    } else {
      // Non-English: try translations JSON first, then legacy per-language column, then base
      const fromTranslations = attraction.translations?.[targetLanguage]?.[fieldName];
      value = fromTranslations || attraction[`${fieldName}_${targetLanguage}`] || attraction[fieldName] || '';
    }

    return value;
  };

  // Function to get array field value (features, facilities, accessibility) for current viewing language
  // eslint-disable-next-line no-unused-vars
  const getArrayFieldValue = (fieldName, attraction = editingAttraction) => {
    if (!attraction) return [];

    let rawValue;
    if (viewingLanguage === 'en') {
      rawValue = attraction[fieldName] || attraction[`${fieldName}_english`];
    } else {
      const fromTranslations = attraction.translations?.[viewingLanguage]?.[fieldName];
      rawValue = fromTranslations || attraction[`${fieldName}_${viewingLanguage}`] || attraction[fieldName];
    }

    if (Array.isArray(rawValue)) {
      return rawValue;
    } else if (typeof rawValue === 'string' && rawValue.trim() !== '') {
      try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  // Initialize form data
  useEffect(() => {
    if (editingAttraction) {
      console.log('🔄 === INITIALIZING FORM WITH EDITING ATTRACTION ===');
      console.log('🎯 Editing attraction:', editingAttraction);
      console.log('🎯 Original language from DB:', editingAttraction.original_language);
      console.log('🎯 Viewing language:', viewingLanguage);
      
      // NOTE: NOT setting source language from DB because data might be corrupted
      // Let user manually select their input language instead
      const originalLang = editingAttraction.original_language || 'english';
      console.log(`📋 DB original_language: ${originalLang} (not auto-setting, user can choose manually)`)
      
      // NOTE: NOT auto-setting viewing language to prevent selector from getting stuck
      // Users can manually select which language they want to view the data in
      console.log(`📋 DB original_language: ${originalLang} (viewing language stays user-controlled)`)
      
      // Transform data for editing - convert objects to arrays
      const apiBase = process.env.REACT_APP_API_URL || '';
      
      // Handle media_links - Sequelize returns it as an object, not a string
      let mediaLinks = [];
      if (editingAttraction.media_links) {
        if (Array.isArray(editingAttraction.media_links)) {
          mediaLinks = editingAttraction.media_links;
        } else if (typeof editingAttraction.media_links === 'string') {
          try {
            mediaLinks = JSON.parse(editingAttraction.media_links);
          } catch (e) {
            console.warn('Failed to parse media_links string:', editingAttraction.media_links);
            mediaLinks = [];
          }
        }
      }
      
      // Convert relative URLs to absolute URLs
      const processedMediaLinks = mediaLinks.map(media => {
        if (media.url && media.url.startsWith('/')) {
          return { ...media, url: `${apiBase}${media.url}` };
        }
        return media;
      });

      // Get language-specific field values
      // const langSuffix = viewingLanguage === 'english' ? '' : `_${viewingLanguage}`; // Unused - kept for potential future use
      
      console.log(`🔄 === UPDATING FORM DATA FOR VIEWING LANGUAGE: ${viewingLanguage} ===`);
      console.log('📋 Available category fields:', {
        base: editingAttraction.category,
        english: editingAttraction.category_english,
        malay: editingAttraction.category_malay,
        arabic: editingAttraction.category_arabic,
        chinese: editingAttraction.category_chinese,
        japanese: editingAttraction.category_japanese
      });
      
      // Debug: Log address fields from API
      console.log('🏠 === ADDRESS FIELDS DEBUG ===');
      console.log('address_1:', editingAttraction.address_1);
      console.log('address_2:', editingAttraction.address_2);
      console.log('city:', editingAttraction.city);
      console.log('state:', editingAttraction.state);
      console.log('country:', editingAttraction.country);
      console.log('postal_code:', editingAttraction.postal_code);
      console.log('district:', editingAttraction.district);
      console.log('Full editingAttraction object keys:', Object.keys(editingAttraction));
      
      const transformedData = {
        ...editingAttraction,
        name: getFieldValue('name'),
        description: getFieldValue('description'),
        long_description: getFieldValue('long_description'),
        keywords: editingAttraction.keywords || editingAttraction.keywords_english || '',
        // Keep base English values for dropdowns, but show translated text in display fields
        category: editingAttraction.category || getFieldValue('category', editingAttraction, 'en'),
        subcategory: editingAttraction.subcategory || getFieldValue('subcategory', editingAttraction, 'en'),
        display_category: (editingAttraction.display_category && editingAttraction.display_category.trim() !== '')
          ? editingAttraction.display_category
          : getFieldValue('display_category', editingAttraction, 'en'),
        address: getFieldValue('address'),
        // Structured address fields - ensure they're properly set even if null/undefined
        address_1: editingAttraction.address_1 !== null && editingAttraction.address_1 !== undefined ? editingAttraction.address_1 : '',
        address_2: editingAttraction.address_2 !== null && editingAttraction.address_2 !== undefined ? editingAttraction.address_2 : '',
        city: editingAttraction.city !== null && editingAttraction.city !== undefined ? editingAttraction.city : '',
        state: editingAttraction.state !== null && editingAttraction.state !== undefined ? editingAttraction.state : '',
        country: editingAttraction.country !== null && editingAttraction.country !== undefined ? editingAttraction.country : '',
        postal_code: editingAttraction.postal_code !== null && editingAttraction.postal_code !== undefined ? editingAttraction.postal_code : '',
        district: editingAttraction.district !== null && editingAttraction.district !== undefined ? String(editingAttraction.district).trim() : '',
        // Explicitly set coordinates so they always appear when editing (spread can miss them)
        latitude: editingAttraction.latitude != null && editingAttraction.latitude !== '' ? String(editingAttraction.latitude) : '',
        longitude: editingAttraction.longitude != null && editingAttraction.longitude !== '' ? String(editingAttraction.longitude) : '',
        tips: getFieldValue('tips'),
        history: getFieldValue('history'),
        pic_contact: getFieldValue('pic_contact'),

        operating_hours: parseOperatingHours(editingAttraction.operating_hours),
        social_links: editingAttraction.social_links || {},
        media_links: processedMediaLinks,
        halal_status: editingAttraction.halal_status || '',
        star_rating: editingAttraction.star_rating ? String(editingAttraction.star_rating) : '',
        orkid_rating: editingAttraction.orkid_rating ? String(editingAttraction.orkid_rating) : '',
      };

      console.log('🏠 === TRANSFORMED ADDRESS FIELDS ===');
      console.log('address_1:', transformedData.address_1);
      console.log('address_2:', transformedData.address_2);
      console.log('city:', transformedData.city);
      console.log('state:', transformedData.state);
      console.log('country:', transformedData.country);
      console.log('postal_code:', transformedData.postal_code);
      
      console.log('🎯 Language-specific transformed data:', {
        name: transformedData.name,
        category: transformedData.category,
        subcategory: transformedData.subcategory,
        display_category: transformedData.display_category
      });
      
      console.log('🔍 DISPLAY_CATEGORY DEBUG:', {
        'raw_from_api': editingAttraction.display_category,
        'type': typeof editingAttraction.display_category,
        'empty_check': editingAttraction.display_category && editingAttraction.display_category.trim() !== '',
        'final_value': transformedData.display_category
      });
      setFormData(transformedData);
      
      // Sync uploadedImages state with formData.media_links for UI consistency
      const imagesForState = processedMediaLinks.map(media => ({
        originalName: media.filename || media.url?.split('/').pop() || 'image',
        filename: media.filename || media.url?.split('/').pop() || 'image',
        url: media.url,
        isLocal: media.isLocal || false
      }));
      setUploadedImages(imagesForState);

      // Fetch stampbook slot assignment — only if the tenant has stampbook enabled
      if (tenantConfig?.features?.stampbook) {
        const fetchSlotInfo = async () => {
          try {
            const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
            const slotRes = await fetch(
              `${process.env.REACT_APP_API_URL || ''}/api/stampbook/slots`,
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            if (slotRes.ok) {
              const slotData = await slotRes.json();
              const slots = slotData.slots || slotData || [];
              const match = slots.find(s => s.attraction_id === editingAttraction.id);
              setStampbookSlotInfo(match || null);
            }
          } catch (err) {
            console.warn('Could not fetch stampbook slots:', err);
            setStampbookSlotInfo(null);
          }
        };
        fetchSlotInfo();
      }
    } else {
      // Reset form for new attraction
      setFormData({
        name: '',
        category: '',
        subcategory: '',
        display_category: '',
        description: '',
        long_description: '',
        keywords: '',
        address: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        country: '',
        postal_code: '',
        district: '',
        latitude: '',
        longitude: '',
        price_adult: '',
        price_child: '',
        price_senior_citizen: '',
        price_tourist: '',
        currency: 'USD',
        pricing_type: 'flat_rate',
        operating_hours: {},
        tips: '',
        history: '',
        website_url: '',
        social_links: {},
        is_bookable_online: false,
        contact_number: '',
        map_url: '',
        media_links: [],
        pic_contact: '',
        halal_status: '',
        star_rating: '',
        orkid_rating: '',
      });
      setStampbookSlotInfo(null);
      // Reset uploadedImages for new attraction
      setUploadedImages([]);
    }
    setErrors({});
    setTranslatedData(null);
    setShowTranslationPreview(false);
    setTranslationError('');
    setIsAddingCategory(false); // reset the "add new category" input each open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingAttraction, isOpen, viewingLanguage, tenantConfig]); // Dependencies for form data update (getFieldValue is stable)

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Clear subcategory when category changes
      if (name === 'category') {
        newData.subcategory = '';
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Category dropdown change: the trailing "➕ Add new category…" sentinel swaps
  // the control for a free-text input; any other value flows through normally.
  const handleCategorySelectChange = (e) => {
    if (e.target.value === '__add_new__') {
      setIsAddingCategory(true);
      setFormData(prev => ({ ...prev, category: '', subcategory: '' }));
      if (errors.category) setErrors(prev => ({ ...prev, category: '' }));
      return;
    }
    handleInputChange(e);
  };



  // Handle social media links changes
  const handleSocialLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: value
      }
    }));
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setIsUploadingImage(true);
    setUploadError('');

    try {
      // Create FormData for file upload
      const formData = new FormData();
      files.forEach(file => {
        formData.append('images', file);
      });

      // Upload files to server using API service
      const result = await uploadAPI.uploadImages(formData);
      
      if (!result.success) {
        throw new Error(result.message || 'Upload failed');
      }

      // Process uploaded files
      const uploadedFiles = result.data.files.map(file => ({
        originalName: file.originalName,
        filename: file.filename,
        url: file.url,
        size: file.size,
        mimeType: file.mimeType,
        uploadedAt: file.uploadedAt,
        isLocal: true
      }));
      
      console.log('📤 Upload Response:', result);
      console.log('🖼️ Processed Files:', uploadedFiles);
      
      // Add to uploaded images
      setUploadedImages(prev => [...prev, ...uploadedFiles]);
      
      // Update form data media_links with the new images
      // IMPORTANT: Preserve ALL metadata from upload response to prevent corruption
      const newMediaLinks = uploadedFiles.map(img => {
        // Ensure URL is absolute
        let imageUrl = img.url;
        if (imageUrl.startsWith('/')) {
          // Convert relative URL to absolute URL using API base
          const apiBase = process.env.REACT_APP_API_URL || '';
          imageUrl = `${apiBase}${imageUrl}`;
        }
        
        // Validate URL is not empty
        if (!imageUrl || imageUrl.trim() === '') {
          console.error('❌ Invalid image URL:', img);
          throw new Error(`Invalid image URL for file: ${img.filename || img.originalName}`);
        }
        
        // Preserve ALL metadata from upload response
        return {
          url: imageUrl,
          type: 'image',
          caption: '',
          filename: img.filename || null,
          isLocal: true,
          // Preserve storage metadata
          storage: img.storage || 'mount',
          s3Url: img.s3Url || null,
          // Preserve other metadata
          originalName: img.originalName || null,
          size: img.size || null,
          mimeType: img.mimeType || null,
          uploadedAt: img.uploadedAt || new Date().toISOString(),
          added_at: img.uploadedAt || new Date().toISOString()
        };
      });
      
      console.log('📝 New Media Links:', newMediaLinks);
      
      // Note: Images are now added to form state only
      // They will be persisted to database when user clicks "Quick Save" or "Translate & Save"
      console.log('ℹ️ Images added to form state - will be saved when form is submitted');
      
      setFormData(prev => {
        console.log('🔍 Before update - prev.media_links:', prev.media_links);
        console.log('🔍 Adding newMediaLinks:', newMediaLinks);
        
        const updatedFormData = {
          ...prev,
          media_links: [
            ...prev.media_links,
            ...newMediaLinks
          ]
        };
        
        console.log('🔍 After update - media_links count:', updatedFormData.media_links.length);
        console.log('📋 Updated Form Data:', updatedFormData);
        return updatedFormData;
      });

      console.log('✅ Images uploaded successfully:', uploadedFiles);

    } catch (error) {
      console.error('Image upload error:', error);
      setUploadError(`Failed to upload images: ${error.message}`);
    } finally {
      setIsUploadingImage(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove uploaded image - persists to DB immediately so deleted images don't reappear on refresh
  const removeImage = async (mediaToRemove) => {
    try {
      let targetMedia;
      if (typeof mediaToRemove === 'number') {
        targetMedia = formData.media_links[mediaToRemove];
      } else {
        targetMedia = mediaToRemove;
      }
      
      if (!targetMedia) {
        console.error('Media to remove not found');
        return;
      }
      
      const mediaUrl = targetMedia.url || targetMedia.s3Url || '';
      const mediaFilename = targetMedia.filename || '';
      console.log('🗑️ Removing image:', { url: mediaUrl, filename: mediaFilename });
      
      // Build new list without this item (used for both state and API)
      const newMediaLinks = (formData.media_links || []).filter(media => {
        const currentUrl = media.url || media.s3Url || '';
        const currentFilename = media.filename || '';
        return !(currentUrl === mediaUrl || currentFilename === mediaFilename);
      });
      
      // Update UI state immediately
      setUploadedImages(prev => prev.filter(img => {
        const imgUrl = img.url || '';
        const imgFilename = img.filename || '';
        return !(imgUrl === mediaUrl || imgFilename === mediaFilename);
      }));
      setFormData(prev => ({ ...prev, media_links: newMediaLinks }));
      
      // Persist to DB first so the removal sticks (prevents old images reappearing)
      const attractionId = editingAttraction?.id;
      if (attractionId) {
        try {
          const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
          const formattedMediaLinks = newMediaLinks.map(link => ({
            type: link.type || 'image',
            url: link.url || link.s3Url || '',
            filename: link.filename || null,
            isLocal: link.isLocal || false,
            s3Url: link.s3Url || null,
            storage: link.storage || 'mount',
            added_at: link.added_at || new Date().toISOString()
          }));
          await api.post(
            `/api/attractions/${attractionId}/save-all-media`,
            { media_links: formattedMediaLinks },
            { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );
          console.log('✅ Image removal saved to database');
        } catch (saveErr) {
          console.error('Failed to save media list after remove:', saveErr);
          // Don't revert UI; user can save again via Save button
        }
      }
      
      // Then delete file from disk (now safe: no longer referenced in DB)
      if (targetMedia.isLocal && targetMedia.filename) {
        try {
          const result = await uploadAPI.deleteFile(targetMedia.filename);
          if (result && result.success) {
            console.log('File deleted from server:', targetMedia.filename);
          }
        } catch (err) {
          console.warn('File delete from server (optional):', err.message);
        }
      }
      
      console.log('✅ Image removed from state and DB');
    } catch (error) {
      console.error('Error removing image:', error);
      if (typeof mediaToRemove === 'number') {
        const targetMedia = formData.media_links[mediaToRemove];
        if (targetMedia) {
          const mediaUrl = targetMedia.url || targetMedia.s3Url || '';
          const mediaFilename = targetMedia.filename || '';
          setUploadedImages(prev => prev.filter(img => (img.url || '') !== mediaUrl && (img.filename || '') !== mediaFilename));
          setFormData(prev => ({
            ...prev,
            media_links: prev.media_links.filter(m => (m.url || m.s3Url || '') !== mediaUrl && (m.filename || '') !== mediaFilename)
          }));
        }
      }
    }
  };

  // Add media link via URL function removed per user request

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate form for quick save (more lenient) - separate from main validation
  const validateFormForQuickSave = () => {
    // For new attractions, backend requires name and category
    // For existing attractions, only name is required
    if (!formData.name || !formData.name.trim()) {
      setQuickSaveError('Name is required for saving');
      return false;
    }
    
    // If creating new attraction, category is also required by backend
    if (!editingAttraction && (!formData.category || !formData.category.trim())) {
      setQuickSaveError('Please select a Category from the dropdown menu. Both Name and Category are required for new attractions.');
      return false;
    }
    
    // Clear any previous quick save errors if validation passes
    setQuickSaveError('');
    return true;
  };

  // Start translation process
  const handleTranslate = async () => {
    if (!validateForm()) {
      alert('Please fix the form errors before translating.');
      return;
    }

    // Force a state update to ensure we have the latest form data
    await new Promise(resolve => {
      setFormData(prevData => {
        // This will trigger a re-render and ensure we have the latest data
        resolve();
        return prevData;
      });
    });

    setIsTranslating(true);
    setTranslationError('');
    setQuickSaveError(''); // Clear any quick save errors when starting translation
    setTranslatedData(null);
    // Reset progress state
    setTranslationProgress({
      step: 'initializing',
      progress: 0,
      currentLanguage: '',
      completedLanguages: 0,
      error: null
    });

    try {
      // Force a fresh read of all form data to ensure we have the latest values
      const getFreshFormData = () => {
        // Start with a clean slate and build from form inputs
        const freshData = {
          name: '',
          category: '',
          subcategory: '',
          display_category: '',
          description: '',
          long_description: '',
          address: '',
          address_1: '',
          address_2: '',
          city: '',
          state: '',
          country: '',
          postal_code: '',
          district: '',
          latitude: '',
          longitude: '',
          price_adult: '',
          price_child: '',
          currency: 'MYR',
          pricing_type: 'flat',
          operating_hours: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
          },
          features: [],
          facilities: [],
          accessibility: [],
          tips: '',
          history: '',
          pic_contact: '',
          website_url: '',
          social_links: {},
          is_bookable_online: false,
          contact_number: '',
          map_url: '',
          media_links: [],
          halal_status: '',
          keywords: '',
          star_rating: '',
          orkid_rating: '',
        };

        // Get form element
        const form = formRef.current || document.querySelector('form');
        if (form) {
          const formElements = form.elements;
          
          // Read all form inputs and populate fresh data
          for (let element of formElements) {
            if (element.name && element.name in freshData) {
              if (element.type === 'checkbox') {
                freshData[element.name] = element.checked;
              } else if (element.type === 'radio') {
                if (element.checked) {
                  freshData[element.name] = element.value;
                }
              } else {
                freshData[element.name] = element.value;
              }
            }
          }
        }
        
        // Also preserve any non-form data like media_links, social_links, etc.
        if (formData.media_links) freshData.media_links = formData.media_links;
        if (formData.social_links) freshData.social_links = formData.social_links;
        if (formData.features) freshData.features = formData.features;
        if (formData.facilities) freshData.facilities = formData.facilities;
        if (formData.accessibility) freshData.accessibility = formData.accessibility;
        // Preserve operating_hours in new JSON format
        if (formData.operating_hours && typeof formData.operating_hours === 'object') {
          freshData.operating_hours = formData.operating_hours;
        }
        
        return freshData;
      };

      const freshFormData = getFreshFormData();

      // Always call the translation API with current form data (both new and existing attractions)
      // This ensures the translation preview shows the latest form changes
      console.log('🔄 === FRESH TRANSLATION REQUEST ===');
      console.log('🔄 Processing attraction with updated form data...');
      console.log('🔄 Original form data:', formData);
      console.log('🔄 Fresh form data:', freshFormData);
      console.log('🔄 Timestamp:', new Date().toISOString());
        
      // Step 1: Geocode address BEFORE translation if coordinates are missing
      let finalFormData = { ...freshFormData };
        
      // Use structured address if available, otherwise fallback to old address field
      const hasStructuredAddress = freshFormData.address_1 || freshFormData.address_2 || freshFormData.city;
      const hasAddress = hasStructuredAddress || freshFormData.address;
        
      if ((!freshFormData.latitude || !freshFormData.longitude) && hasAddress) {
        console.log('🌍 Geocoding address before translation...');
        try {
          const geocodeResponse = await fetch('/api/attractions/geocode', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              address: freshFormData.address, // Keep for backward compatibility
              address_1: freshFormData.address_1,
              address_2: freshFormData.address_2,
              city: freshFormData.city,
              state: freshFormData.state || undefined,
              country: freshFormData.country || undefined,
              postal_code: freshFormData.postal_code,
              district: freshFormData.district
            })
          });
          
          if (geocodeResponse.ok) {
            const geocodeData = await geocodeResponse.json();
            if (geocodeData.success && geocodeData.coordinates) {
              finalFormData.latitude = geocodeData.coordinates.latitude;
              finalFormData.longitude = geocodeData.coordinates.longitude;
              console.log('✅ Geocoding successful:', geocodeData.coordinates);
            }
          }
        } catch (geocodeError) {
          console.log('⚠️ Geocoding failed, proceeding without coordinates:', geocodeError.message);
        }
      }

      // Step 2: Prepare data for translation
      // OPTIMIZED: Send only essential translatable fields to prevent timeout
      const dataToTranslate = {
        name: finalFormData.name,
        description: finalFormData.description,
        long_description: finalFormData.long_description,
        category: finalFormData.category,
        subcategory: finalFormData.subcategory,
        display_category: finalFormData.display_category,
        address: finalFormData.address,
        district: finalFormData.district,
        tips: finalFormData.tips,
        history: finalFormData.history,
        pic_contact: finalFormData.pic_contact,
        operating_hours: finalFormData.operating_hours, // New JSON format
        // REMOVED: Non-essential fields that cause timeout
        // - features, facilities, accessibility (arrays)
        // - latitude, longitude (coordinates)
        // - currency, pricing_type (pricing)
        // - website_url, social_links, map_url (URLs)
        // - is_bookable_online, contact_number (booleans/strings)
        // - media_links (complex objects)
        // - translation_timestamp, force_refresh (metadata)
      };

      // Force correct source language based on content
      const actualSourceLanguage = getCorrectSourceLanguage(dataToTranslate);
      
      console.log(`🔍 Source Language Detection:
        User Selected: ${sourceLanguage}
        Auto-Detected: ${actualSourceLanguage}
        Content Sample: "${(dataToTranslate.description || '').substring(0, 50)}..."`);

      console.log('🔄 === SENDING DATA TO TRANSLATION API ===');
      console.log('🔄 Data to translate:', JSON.stringify(dataToTranslate, null, 2));
      console.log('🔄 Source language:', actualSourceLanguage);

      const result = await translationAPI.translateAttractionWithProgress(
        dataToTranslate,
        actualSourceLanguage,
        setTranslationProgress
      );

      console.log('🔍 EnhancedAttractionForm - Translation API Result:');
      console.log('Full Result:', result);
      console.log('Translated Data:', result.data?.translatedData);
      
      if (result.data?.translatedData) {
        console.log('✅ Translation data received successfully');
        console.log('📊 Translated data keys:', Object.keys(result.data.translatedData));

        // Build preview data: en source + existing translations as base + fresh translate on top
        const previewData = {};

        // 1. Inject English source from form fields
        previewData.en = {
          name: finalFormData.name,
          description: finalFormData.description,
          long_description: finalFormData.long_description,
          category: finalFormData.category,
          subcategory: finalFormData.subcategory,
          display_category: finalFormData.display_category,
          address: finalFormData.address,
          district: finalFormData.district,
          tips: finalFormData.tips,
          history: finalFormData.history,
          pic_contact: finalFormData.pic_contact,
          operating_hours: finalFormData.operating_hours,
        };

        // 2. Hydrate from existing translations column (base layer)
        const existingTranslations = editingAttraction?.translations || {};
        for (const langCode of supportedCodes) {
          if (langCode === 'en') continue;
          if (existingTranslations[langCode]) {
            previewData[langCode] = { ...existingTranslations[langCode] };
          }
        }

        // 3. Overlay fresh translate result (takes precedence)
        for (const [langCode, langFields] of Object.entries(result.data.translatedData)) {
          previewData[langCode] = { ...(previewData[langCode] || {}), ...langFields };
        }

        // 4. For non-English tabs, fill non-translated fields from English source
        const nonTranslatableFields = ['name', 'category', 'subcategory', 'display_category', 'address', 'district', 'operating_hours', 'pic_contact'];
        for (const langCode of supportedCodes) {
          if (langCode === 'en') continue;
          if (!previewData[langCode]) previewData[langCode] = {};
          for (const field of nonTranslatableFields) {
            if (!previewData[langCode][field]) {
              previewData[langCode][field] = previewData.en[field];
            }
          }
        }

        console.log('🔄 Setting translatedData state with merged preview data...');
        setTranslatedData(previewData);
        
        // CRITICAL: Set preview state BEFORE hiding loading screen to prevent blank page
        setShowTranslationPreview(true);
        
        // Use setTimeout to ensure state updates are applied before hiding loading screen
        setTimeout(() => {
          setIsTranslating(false);
          console.log('✅ Translation preview should now be visible');
        }, 100);
      } else {
        console.log('❌ No translation data in API result');
        console.log('❌ Result structure:', result);
        setTranslationError('Translation API returned no data. Please check if the translation service is available.');
        setShowTranslationPreview(false);
        setTranslatedData(null);
        setIsTranslating(false);
      }

    } catch (error) {
      console.error('Translation failed:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Translation failed. ';
      if (error.response?.status === 404) {
        errorMessage += 'Translation service is not available. Please contact the administrator.';
      } else if (error.response?.status >= 500) {
        errorMessage += 'Translation service is experiencing issues. Please try again later.';
      } else if (error.message.includes('timeout')) {
        errorMessage += 'Translation is taking too long. Please try again.';
      } else {
        errorMessage += error.message;
      }
      
      // Update progress to show error state
      setTranslationProgress(prev => ({
        ...prev,
        step: 'error',
        error: errorMessage
      }));
      
      // Set error message immediately
      setTranslationError(errorMessage);
      
      // Keep loading screen visible for 3 seconds to show error, then hide and show form
      setTimeout(() => {
        setIsTranslating(false);
        setShowTranslationPreview(false);
        setTranslatedData(null);
        setTranslationProgress({
          step: 'initializing',
          progress: 0,
          currentLanguage: '',
          completedLanguages: 0,
          error: null
        });
        // Form will be shown automatically since isTranslating is false and showTranslationPreview is false
      }, 3000);
    }
  };

  // Approve and save translations
  const handleApproveTranslations = async (approvedTranslations, editedLangs = []) => {
    setIsSaving(true);
    const startTime = Date.now();
    // overwriteLanguages is driven SOLELY by which languages the admin hand-edited
    // in the preview (editedLangs) — never by which languages MT produced. A plain
    // Translate All with no manual edit therefore sends [] → the backend B2 guard
    // keeps all populated (reviewed) translations untouched (fill-empty-only).
    const overwriteLanguages = Array.isArray(editedLangs) ? editedLangs.filter(l => l && l !== 'en') : [];
    console.log('🖊️ Hand-edited languages (overwriteLanguages):', overwriteLanguages);
    
    try {
      const attractionId = editingAttraction?.id || null;
      
      console.log('🚀 ======== APPROVE & SAVE BUTTON CLICKED ========');
      console.log('⏰ Timestamp:', new Date().toISOString());
      console.log('🎯 Mode:', attractionId ? 'UPDATE' : 'CREATE');
      console.log('🎯 Attraction ID:', attractionId);
      console.log('🎯 Source Language:', sourceLanguage);
      console.log('🎯 Viewing Language:', viewingLanguage);
      
      // 🚨 CRITICAL CHECK: Are we bypassing translation?
      console.log('\n🚨 === TRANSLATION BYPASS CHECK ===');
      console.log('🔍 approvedTranslations keys:', Object.keys(approvedTranslations || {}));
      
      if (approvedTranslations && approvedTranslations.en) {
        const englishData = approvedTranslations.en;
        const sampleText = (englishData.description || englishData.name || '').toLowerCase();
        
        // Check for Malay words in what should be English data
        const malayWords = ['adalah', 'yang', 'dengan', 'untuk', 'pada', 'di', 'ini', 'telah', 'akan', 'syarikat', 'terletak'];
        const foundMalayWords = malayWords.filter(word => sampleText.includes(word));
        
        if (foundMalayWords.length > 0) {
          console.log('🚨 ⚠️  FRONTEND ALERT: MALAY WORDS IN "ENGLISH" DATA!');
          console.log('🚨 ⚠️  This means translation was skipped or failed!');
          console.log('🚨 ⚠️  Found Malay words:', foundMalayWords);
          console.log('🚨 ⚠️  English data sample:', (englishData.description || englishData.name || '').substring(0, 100));
          console.log('🚨 ⚠️  This suggests form input data was copied directly to save API');
        } else {
          console.log('✅ English data appears properly translated');
          console.log('✅ English sample:', (englishData.description || englishData.name || '').substring(0, 50));
        }
      } else {
        console.log('❌ No English (en) data in approved translations!');
        console.log('❌ This means translation step was completely bypassed!');
      }
      
      console.log('\n📊 === FORM DATA ANALYSIS ===');
      console.log('🎯 Form Data Keys:', Object.keys(formData));
      console.log('🎯 Media Links:', formData.media_links);
      console.log('🎯 Media Links Count:', formData.media_links?.length || 0);
      console.log('🎯 Media Links Types:', formData.media_links?.map(m => m.type));
      console.log('🎯 Operating Hours:', formData.operating_hours);
      console.log('🎯 Social Links:', formData.social_links);
      console.log('🎯 Pricing Info:', { 
        price_adult: formData.price_adult, 
        price_child: formData.price_child,
        price_senior_citizen: formData.price_senior_citizen,
        price_tourist: formData.price_tourist,
        currency: formData.currency,
        pricing_type: formData.pricing_type 
      });
      
      console.log('\n📝 === TRANSLATION DATA ANALYSIS ===');
      console.log('🎯 Approved Translations Keys:', Object.keys(approvedTranslations));
      console.log('🎯 Languages Available:', Object.keys(approvedTranslations).join(', '));
      
      // Log each language's data
      Object.keys(approvedTranslations).forEach(lang => {
        console.log(`🌐 ${lang.toUpperCase()}:`, {
          name: approvedTranslations[lang]?.name,
          category: approvedTranslations[lang]?.category,
          subcategory: approvedTranslations[lang]?.subcategory,
          description_length: approvedTranslations[lang]?.description?.length || 0
        });
      });
      
      console.log('\n📤 === PREPARING API REQUEST ===');
      
      // 🚨 CRITICAL FIX: DO NOT override translated English data with form data!
      // The approvedTranslations already contains properly translated English from backend
      console.log('🔧 Using translated English data from backend, NOT overriding with form data');
      console.log('🔧 English translation sample:', approvedTranslations.en?.description?.substring(0, 100) + '...');
      
      // Clean formData to remove empty price fields before passing as attractionData
      const cleanedFormData = {
        ...formData,
        // Price fields are completely removed from translation system
      };
      
      const result = await translationAPI.saveTranslatedAttraction(
        attractionId,
        approvedTranslations, // Use ONLY the approved translations (includes clean English)
        cleanedFormData, // Pass the cleaned form data as attractionData for reference
        sourceLanguage,
        overwriteLanguages // per-language opt-in: only hand-edited langs get overwritten
      );
      
      console.log('\n✅ === API SAVE RESPONSE ===');
      console.log('🎯 Save Result:', result);
      console.log('🎯 Success:', result?.success);
      console.log('🎯 Message:', result?.message);
      console.log('🎯 Data:', result?.data);
      console.log('⏱️ Save Duration:', `${Date.now() - startTime}ms`);

      // 🖼️ SAVE ALL MEDIA LINKS AT ONCE (same as quick save to prevent duplicates)
      // ALWAYS save media_links, even if empty (to clear deleted images)
      // Backend will REPLACE existing media_links with this array
      if (attractionId) {
        console.log('\n🖼️ === SAVING ALL MEDIA LINKS AT ONCE (TRANSLATE & SAVE) ===');
        const mediaLinksToSave = formData.media_links || [];
        console.log('🖼️ Total media links to save:', mediaLinksToSave.length);
        console.log('🖼️ Media links array:', mediaLinksToSave);
        console.log('🖼️ IMPORTANT: Sending complete media_links array to REPLACE existing (not merge)');
        
        try {
          // Verify token exists before making request
          const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
          if (!token) {
            console.error('❌ No authentication token found for media save');
            return;
          }
          
          // Ensure media links are properly formatted
          const formattedMediaLinks = mediaLinksToSave.map(link => ({
            type: link.type || 'image',
            url: link.url || link.s3Url || '',
            filename: link.filename || null,
            isLocal: link.isLocal || false,
            s3Url: link.s3Url || null,
            storage: link.storage || 'mount',
            added_at: link.added_at || new Date().toISOString()
          }));
          
          console.log('💾 Sending media links to backend (Translate & Save):', {
            count: formattedMediaLinks.length,
            hasToken: !!token,
            tokenLength: token ? token.length : 0,
            note: formattedMediaLinks.length === 0 ? 'Empty array - will clear all images' : 'Will replace existing images'
          });
          
          // Make the request - ensure token is in headers
          // Pass headers directly in the config object
          const response = await api.post(
            `/api/attractions/${attractionId}/save-all-media`,
            { media_links: formattedMediaLinks },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('✅ All media links saved successfully:', response.data);
          console.log('✅ Saved media count:', response.data?.total_media_count || 0);
        } catch (mediaError) {
          console.error('❌ Failed to save media links:', mediaError);
          console.error('❌ Error details:', {
            message: mediaError.message,
            response: mediaError.response?.data,
            status: mediaError.response?.status,
            statusText: mediaError.response?.statusText
          });
          // Don't fail the whole operation, just log the error
        }
      } else {
        console.log('ℹ️ No attraction ID - skipping media links save');
      }

      // Call parent save callback
      if (onSave) {
        onSave(result.data);
      }

      // Reset translation preview state before closing
      setShowTranslationPreview(false);
      setTranslatedData(null);
      setTranslationError('');
      setIsSaving(false);
      
      alert('Attraction saved successfully in all languages!');
      
      // Small delay to ensure state is reset before closing
      setTimeout(() => {
        onClose();
      }, 100);

    } catch (error) {
      console.log('\n❌ === SAVE PROCESS FAILED ===');
      console.log('⏰ Error Timestamp:', new Date().toISOString());
      console.log('❌ Error Message:', error.message);
      console.log('❌ Error Object:', error);
      console.log('❌ Error Stack:', error.stack);
      console.log('⏱️ Total Duration:', `${Date.now() - startTime}ms`);
      
      if (error.response) {
        console.log('📥 Server Response:', error.response.data);
        console.log('📥 Status Code:', error.response.status);
        console.log('📥 Response Headers:', error.response.headers);
      }
      
      console.log('🚀 ======== SAVE PROCESS ENDED WITH ERROR ========\n');
      console.error('Save failed:', error);
      
      // Don't close the modal on error - let user try again
      setShowTranslationPreview(true); // Keep preview open so user can see what they were trying to save
      alert(`Failed to save attraction: ${error.message}\n\nPlease check the console for details and try again.`);
    } finally {
      console.log('\n🏁 === SAVE PROCESS COMPLETED ===');
      console.log('⏱️ Total Process Duration:', `${Date.now() - startTime}ms`);
      console.log('🚀 ======== SAVE PROCESS FINISHED ========\n');
      setIsSaving(false);
    }
  };

  // Handle translation workflow (only way to save)
  const handleTranslationWorkflow = async () => {
    if (!validateForm()) {
      return;
    }

    // Start the translation process
    await handleTranslate();
  };

  // Review / Edit existing translations — opens preview from stored data, NO translate API call
  const handleReviewTranslations = () => {
    const previewData = {};

    // 1. English source from current form fields
    previewData.en = {
      name: formData.name,
      description: formData.description,
      long_description: formData.long_description,
      category: formData.category,
      subcategory: formData.subcategory,
      display_category: formData.display_category,
      address: formData.address,
      district: formData.district,
      tips: formData.tips,
      history: formData.history,
      pic_contact: formData.pic_contact,
      operating_hours: formData.operating_hours,
    };

    // 2. Hydrate each supported language from stored translations column
    //    Only use tenant config ISO codes — ignore legacy language-name keys
    //    (e.g. 'english', 'finnish') that may exist in stored data.
    const existingTranslations = editingAttraction?.translations || {};
    const allLangCodes = supportedCodes.filter(c => c !== 'en');
    const nonTranslatableFields = ['name', 'category', 'subcategory', 'display_category', 'address', 'district', 'operating_hours', 'pic_contact'];
    for (const langCode of allLangCodes) {
      previewData[langCode] = { ...(existingTranslations[langCode] || {}) };
      // Fill non-translated fields from English source
      for (const field of nonTranslatableFields) {
        if (!previewData[langCode][field]) {
          previewData[langCode][field] = previewData.en[field];
        }
      }
    }

    // Open preview — no loading screen, no API call
    setTranslatedData(previewData);
    setShowTranslationPreview(true);
  };

  // Quick save - just save the form data and images without translation
  const handleQuickSave = async () => {
    console.log('💾 === QUICK SAVE INITIATED ===');
    console.log('💾 This operation should NOT involve any translation logic');
    
    // Force clear translation error immediately
    setTranslationError('');
    
    // Wait a bit to ensure React state is updated
    await new Promise(resolve => setTimeout(resolve, 0));
    
    // Clear ALL error states before starting quick save
    setQuickSaveError('');
    setErrors({});
    
    if (!validateFormForQuickSave()) {
      console.log('💾 Quick save validation failed - stopping operation');
      setIsSubmitting(false);
      return;
    }

    console.log('💾 Quick save validation passed - proceeding with save');
    setIsSubmitting(true);
    
    // Track if media save fails
    let mediaSaveFailed = false;
    
    try {
      console.log('💾 === QUICK SAVE: Starting save process ===');
      console.log('💾 Form data:', formData);
      console.log('💾 Media links count:', formData.media_links?.length || 0);

      let attractionId = formData.id;

      if (editingAttraction) {
        // Update existing attraction
        console.log('💾 Updating existing attraction:', attractionId);
        
        // Read lat/long from form inputs so we never miss values (React state can be one tick behind)
        let latForSave = formData.latitude;
        let lngForSave = formData.longitude;
        const formEl = formRef.current || document.querySelector('form');
        if (formEl?.elements?.latitude?.value !== undefined && String(formEl.elements.latitude.value).trim() !== '') {
          latForSave = formEl.elements.latitude.value;
        }
        if (formEl?.elements?.longitude?.value !== undefined && String(formEl.elements.longitude.value).trim() !== '') {
          lngForSave = formEl.elements.longitude.value;
        }
        
        // Create minimal payload for Quick Save (only basic attraction fields)
        // Default display_category to "Hotels" for Accommodation category if empty
        const defaultDisplayCategory = (formData.category === 'Accommodation' && !formData.display_category) ? 'Hotels' : (formData.display_category || '');
        
        // Build complete form data - include ALL fields that can be edited
        const minimalFormData = {
          name: formData.name || '',
          description: formData.description || '',
          category: formData.category || '',
          pricing_type: formData.pricing_type || 'flat_rate',
          currency: formData.currency || 'USD',
          halal_status: formData.halal_status && ['halal', 'non-halal', 'unknown', 'muslim-friendly', 'vegetarian', 'vegan'].includes(formData.halal_status) ? formData.halal_status : 'unknown',
          is_bookable_online: formData.is_bookable_online || false,
        };

        // Add star_rating and orkid_rating for Accommodation
        if (formData.category === 'Accommodation' && formData.star_rating) {
          minimalFormData.star_rating = parseInt(formData.star_rating, 10);
        }
        if (formData.category === 'Accommodation' && formData.orkid_rating) {
          minimalFormData.orkid_rating = parseInt(formData.orkid_rating, 10);
        }
        
        // Price fields - include all pricing fields
        if (formData.price_adult !== undefined && formData.price_adult !== null && formData.price_adult !== '') {
          minimalFormData.price_adult = parseFloat(formData.price_adult);
        }
        if (formData.price_child !== undefined && formData.price_child !== null && formData.price_child !== '') {
          minimalFormData.price_child = parseFloat(formData.price_child);
        }
        if (formData.price_senior_citizen !== undefined && formData.price_senior_citizen !== null && formData.price_senior_citizen !== '') {
          minimalFormData.price_senior_citizen = parseFloat(formData.price_senior_citizen);
        }
        if (formData.price_tourist !== undefined && formData.price_tourist !== null && formData.price_tourist !== '') {
          minimalFormData.price_tourist = parseFloat(formData.price_tourist);
        }
        
        // Social links - include if it's an object with any values
        if (formData.social_links && typeof formData.social_links === 'object' && Object.keys(formData.social_links).length > 0) {
          minimalFormData.social_links = formData.social_links;
        }
        
        // Only add optional fields if they have values (not empty strings)
        if (formData.long_description && formData.long_description.trim()) {
          minimalFormData.long_description = formData.long_description;
        }
        if (formData.keywords && formData.keywords.trim()) {
          minimalFormData.keywords = formData.keywords.trim();
        }
        if (formData.subcategory && formData.subcategory.trim()) {
          minimalFormData.subcategory = formData.subcategory;
        }
        if (defaultDisplayCategory && defaultDisplayCategory.trim()) {
          minimalFormData.display_category = defaultDisplayCategory;
        }
        // New structured address fields
        if (formData.address_1 && formData.address_1.trim()) {
          minimalFormData.address_1 = formData.address_1.trim();
        }
        if (formData.address_2 && formData.address_2.trim()) {
          minimalFormData.address_2 = formData.address_2.trim();
        }
        if (formData.city && formData.city.trim()) {
          minimalFormData.city = formData.city.trim();
        }
        if (formData.state && formData.state.trim()) {
          minimalFormData.state = formData.state.trim();
        }
        if (formData.country && formData.country.trim()) {
          minimalFormData.country = formData.country.trim();
        }
        if (formData.postal_code && formData.postal_code.trim()) {
          minimalFormData.postal_code = formData.postal_code.trim();
        }
        // Keep old address field for backward compatibility (optional)
        if (formData.address && formData.address.trim()) {
          minimalFormData.address = formData.address;
        }
        if (formData.district && formData.district.trim()) {
          minimalFormData.district = formData.district.trim();
        }
        // Operating hours in new JSON format
        if (formData.operating_hours && typeof formData.operating_hours === 'object') {
          // Check if any day has time slots
          const hasTimeSlots = Object.values(formData.operating_hours).some(day => 
            Array.isArray(day) && day.length > 0
          );
          if (hasTimeSlots) {
            minimalFormData.operating_hours = formData.operating_hours;
          }
        }
        if (formData.tips && formData.tips.trim()) {
          minimalFormData.tips = formData.tips;
        }
        if (formData.history && formData.history.trim()) {
          minimalFormData.history = formData.history;
        }
        if (formData.pic_contact && formData.pic_contact.trim()) {
          minimalFormData.pic_contact = formData.pic_contact;
        }
        if (formData.contact_number && formData.contact_number.trim()) {
          minimalFormData.contact_number = formData.contact_number;
        }
        
        // Only add URLs if they're valid
        if (formData.website_url && formData.website_url.trim() && formData.website_url.startsWith('http')) {
          minimalFormData.website_url = formData.website_url;
        }
        if (formData.map_url && formData.map_url.trim() && formData.map_url.startsWith('http')) {
          minimalFormData.map_url = formData.map_url;
        }
        
        // Parse coordinates from form (use latForSave/lngForSave so we have latest from inputs); include if valid
        const latVal = latForSave !== '' && latForSave !== undefined && latForSave !== null
          ? parseFloat(latForSave)
          : NaN;
        const lngVal = lngForSave !== '' && lngForSave !== undefined && lngForSave !== null
          ? parseFloat(lngForSave)
          : NaN;
        if (!isNaN(latVal) && latVal >= -90 && latVal <= 90) {
          minimalFormData.latitude = latVal;
        }
        if (!isNaN(lngVal) && lngVal >= -180 && lngVal <= 180) {
          minimalFormData.longitude = lngVal;
        }
        
        // Price fields are completely removed from translation system
        
        // Convert arrays to proper objects for features, facilities, accessibility
        // Backend expects JSON objects, not arrays
        if (formData.features && Array.isArray(formData.features) && formData.features.length > 0) {
          const featuresObj = {};
          formData.features.forEach((feature, index) => {
            featuresObj[`feature_${index + 1}`] = feature;
          });
          minimalFormData.features = featuresObj;
        }
        if (formData.facilities && Array.isArray(formData.facilities) && formData.facilities.length > 0) {
          const facilitiesObj = {};
          formData.facilities.forEach((facility, index) => {
            facilitiesObj[`facility_${index + 1}`] = facility;
          });
          minimalFormData.facilities = facilitiesObj;
        }
        if (formData.accessibility && Array.isArray(formData.accessibility) && formData.accessibility.length > 0) {
          const accessibilityObj = {};
          formData.accessibility.forEach((access, index) => {
            accessibilityObj[`accessibility_${index + 1}`] = access;
          });
          minimalFormData.accessibility = accessibilityObj;
        }
        
        console.log('💾 Minimal form data for Quick Save:', minimalFormData);
        
        const { attractionsAPI } = await import('../services/api');
        const updateResult = await attractionsAPI.update(attractionId, minimalFormData);
        console.log('💾 Update result:', updateResult);
        
        // Save all media links at once - ALWAYS send the array (even if empty) to ensure deletions are persisted
        const mediaLinksToSave = formData.media_links || [];
        console.log('💾 Saving all media links at once...');
        console.log('💾 Total media links to save:', mediaLinksToSave.length);
        console.log('💾 Media links array:', mediaLinksToSave);
        console.log('💾 IMPORTANT: Sending complete media_links array to REPLACE existing (not merge)');
        
        // ALWAYS send media_links array, even if empty (to clear deleted images)
        // Backend will REPLACE existing media_links with this array
        try {
          // Verify token exists before making request
          const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
          if (!token) {
            throw new Error('No authentication token found. Please log in again.');
          }
          
          // Ensure media links are properly formatted
          const formattedMediaLinks = mediaLinksToSave.map(link => ({
            type: link.type || 'image',
            url: link.url || link.s3Url || '',
            filename: link.filename || null,
            isLocal: link.isLocal || false,
            s3Url: link.s3Url || null,
            storage: link.storage || 'mount',
            added_at: link.added_at || new Date().toISOString()
          }));
          
          console.log('💾 Sending media links to backend:', {
            count: formattedMediaLinks.length,
            attractionId: attractionId,
            hasToken: !!token,
            tokenLength: token ? token.length : 0,
            tokenPrefix: token ? token.substring(0, 20) + '...' : 'none',
            sampleLink: formattedMediaLinks[0],
            note: formattedMediaLinks.length === 0 ? 'Empty array - will clear all images' : 'Will replace existing images'
          });
          
          // Make the request - ensure token is in headers
          // IMPORTANT: Set headers directly on config to ensure they're not lost
          const response = await api.post(
            `/api/attractions/${attractionId}/save-all-media`,
            { media_links: formattedMediaLinks },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('✅ Request completed successfully:', {
            status: response.status,
            hasData: !!response.data,
            savedCount: response.data?.total_media_count || 0
          });
          
          console.log('✅ All media saved at once:', response.data);
          
          if (response.data.warnings) {
            console.warn('⚠️ Backend warnings:', response.data.warnings);
          }
        } catch (mediaError) {
          console.error('⚠️ Failed to save media links (attraction data was saved):', mediaError);
          console.error('💾 Error details:', {
            message: mediaError.message,
            response: mediaError.response?.data,
            status: mediaError.response?.status,
            statusText: mediaError.response?.statusText,
            headers: mediaError.response?.headers,
            mediaLinksCount: mediaLinksToSave.length,
            requestUrl: mediaError.config?.url,
            requestHeaders: mediaError.config?.headers
          });
          
          // Extract error message
          let errorMessage = 'Failed to save media links';
          if (mediaError.response?.data?.message) {
            errorMessage = mediaError.response.data.message;
          } else if (mediaError.message) {
            errorMessage = mediaError.message;
          }
          
          // Don't throw - the main save succeeded, just log the media error
          // We'll show a warning message to the user
          mediaSaveFailed = true;
          setQuickSaveError(`Attraction saved successfully, but images could not be saved: ${errorMessage}. Please try uploading images again.`);
        }
      } else {
        // Create new attraction
        console.log('💾 Creating new attraction');
        
        // Read lat/long from form inputs so we never miss values
        let latForSave = formData.latitude;
        let lngForSave = formData.longitude;
        const formElCreate = formRef.current || document.querySelector('form');
        if (formElCreate?.elements?.latitude?.value !== undefined && String(formElCreate.elements.latitude.value).trim() !== '') {
          latForSave = formElCreate.elements.latitude.value;
        }
        if (formElCreate?.elements?.longitude?.value !== undefined && String(formElCreate.elements.longitude.value).trim() !== '') {
          lngForSave = formElCreate.elements.longitude.value;
        }
        
        // Create minimal payload for Quick Save (only basic attraction fields)
        // Default display_category based on category if empty
        let defaultDisplayCategory = formData.display_category || '';
        if (!defaultDisplayCategory) {
          if (formData.category === 'Accommodation') {
            defaultDisplayCategory = 'Hotels';
          } else if (formData.category === 'Restaurant') {
            defaultDisplayCategory = 'Food';
          } else if (formData.category === 'Event') {
            defaultDisplayCategory = 'Events';
          } else {
            defaultDisplayCategory = 'Places';
          }
        }
        
        // Build complete form data - include ALL fields that can be edited
        const minimalFormData = {
          name: formData.name || '',
          description: formData.description || '',
          category: formData.category || 'Attraction', // Default to 'Attraction' if empty
          pricing_type: formData.pricing_type || 'flat_rate',
          currency: formData.currency || 'USD',
          halal_status: formData.halal_status && ['halal', 'non-halal', 'unknown', 'muslim-friendly', 'vegetarian', 'vegan'].includes(formData.halal_status) ? formData.halal_status : 'unknown',
          is_bookable_online: formData.is_bookable_online || false,
        };

        // Add star_rating and orkid_rating for Accommodation
        if (formData.category === 'Accommodation' && formData.star_rating) {
          minimalFormData.star_rating = parseInt(formData.star_rating, 10);
        }
        if (formData.category === 'Accommodation' && formData.orkid_rating) {
          minimalFormData.orkid_rating = parseInt(formData.orkid_rating, 10);
        }
        
        // Price fields - include all pricing fields
        if (formData.price_adult !== undefined && formData.price_adult !== null && formData.price_adult !== '') {
          minimalFormData.price_adult = parseFloat(formData.price_adult);
        }
        if (formData.price_child !== undefined && formData.price_child !== null && formData.price_child !== '') {
          minimalFormData.price_child = parseFloat(formData.price_child);
        }
        if (formData.price_senior_citizen !== undefined && formData.price_senior_citizen !== null && formData.price_senior_citizen !== '') {
          minimalFormData.price_senior_citizen = parseFloat(formData.price_senior_citizen);
        }
        if (formData.price_tourist !== undefined && formData.price_tourist !== null && formData.price_tourist !== '') {
          minimalFormData.price_tourist = parseFloat(formData.price_tourist);
        }
        
        // Social links - include if it's an object with any values
        if (formData.social_links && typeof formData.social_links === 'object' && Object.keys(formData.social_links).length > 0) {
          minimalFormData.social_links = formData.social_links;
        }
        
        // Only add optional fields if they have values (not empty strings)
        if (formData.long_description && formData.long_description.trim()) {
          minimalFormData.long_description = formData.long_description;
        }
        if (formData.keywords && formData.keywords.trim()) {
          minimalFormData.keywords = formData.keywords.trim();
        }
        if (formData.subcategory && formData.subcategory.trim()) {
          minimalFormData.subcategory = formData.subcategory;
        }
        if (defaultDisplayCategory && defaultDisplayCategory.trim()) {
          minimalFormData.display_category = defaultDisplayCategory;
        }
        // New structured address fields
        if (formData.address_1 && formData.address_1.trim()) {
          minimalFormData.address_1 = formData.address_1.trim();
        }
        if (formData.address_2 && formData.address_2.trim()) {
          minimalFormData.address_2 = formData.address_2.trim();
        }
        if (formData.city && formData.city.trim()) {
          minimalFormData.city = formData.city.trim();
        }
        if (formData.state && formData.state.trim()) {
          minimalFormData.state = formData.state.trim();
        }
        if (formData.country && formData.country.trim()) {
          minimalFormData.country = formData.country.trim();
        }
        if (formData.postal_code && formData.postal_code.trim()) {
          minimalFormData.postal_code = formData.postal_code.trim();
        }
        // Keep old address field for backward compatibility (optional)
        if (formData.address && formData.address.trim()) {
          minimalFormData.address = formData.address;
        }
        if (formData.district && formData.district.trim()) {
          minimalFormData.district = formData.district.trim();
        }
        // Operating hours in new JSON format
        if (formData.operating_hours && typeof formData.operating_hours === 'object') {
          // Check if any day has time slots
          const hasTimeSlots = Object.values(formData.operating_hours).some(day => 
            Array.isArray(day) && day.length > 0
          );
          if (hasTimeSlots) {
            minimalFormData.operating_hours = formData.operating_hours;
          }
        }
        if (formData.tips && formData.tips.trim()) {
          minimalFormData.tips = formData.tips;
        }
        if (formData.history && formData.history.trim()) {
          minimalFormData.history = formData.history;
        }
        if (formData.pic_contact && formData.pic_contact.trim()) {
          minimalFormData.pic_contact = formData.pic_contact;
        }
        if (formData.contact_number && formData.contact_number.trim()) {
          minimalFormData.contact_number = formData.contact_number;
        }
        
        // Only add URLs if they're valid
        if (formData.website_url && formData.website_url.trim() && formData.website_url.startsWith('http')) {
          minimalFormData.website_url = formData.website_url;
        }
        if (formData.map_url && formData.map_url.trim() && formData.map_url.startsWith('http')) {
          minimalFormData.map_url = formData.map_url;
        }
        
        // Parse coordinates (use latForSave/lngForSave from form inputs); include if valid
        const latVal = latForSave !== '' && latForSave !== undefined && latForSave !== null
          ? parseFloat(latForSave)
          : NaN;
        const lngVal = lngForSave !== '' && lngForSave !== undefined && lngForSave !== null
          ? parseFloat(lngForSave)
          : NaN;
        if (!isNaN(latVal) && latVal >= -90 && latVal <= 90) {
          minimalFormData.latitude = latVal;
        }
        if (!isNaN(lngVal) && lngVal >= -180 && lngVal <= 180) {
          minimalFormData.longitude = lngVal;
        }
        
        // Price fields are completely removed from translation system
        
        // Convert arrays to proper objects for features, facilities, accessibility
        // Backend expects JSON objects, not arrays
        if (formData.features && Array.isArray(formData.features) && formData.features.length > 0) {
          const featuresObj = {};
          formData.features.forEach((feature, index) => {
            featuresObj[`feature_${index + 1}`] = feature;
          });
          minimalFormData.features = featuresObj;
        }
        if (formData.facilities && Array.isArray(formData.facilities) && formData.facilities.length > 0) {
          const facilitiesObj = {};
          formData.facilities.forEach((facility, index) => {
            facilitiesObj[`facility_${index + 1}`] = facility;
          });
          minimalFormData.facilities = facilitiesObj;
        }
        if (formData.accessibility && Array.isArray(formData.accessibility) && formData.accessibility.length > 0) {
          const accessibilityObj = {};
          formData.accessibility.forEach((access, index) => {
            accessibilityObj[`accessibility_${index + 1}`] = access;
          });
          minimalFormData.accessibility = accessibilityObj;
        }
        
        console.log('💾 Minimal form data for Quick Save:', minimalFormData);
        console.log('💾 Category value being sent:', minimalFormData.category);
        console.log('💾 Name value being sent:', minimalFormData.name);
        console.log('💾 Description value being sent:', minimalFormData.description);
        console.log('💾 Full payload being sent to API:', JSON.stringify(minimalFormData, null, 2));
        
        const { attractionsAPI } = await import('../services/api');
        const createResult = await attractionsAPI.create(minimalFormData);
        console.log('💾 Create result:', createResult);
        
        attractionId = createResult.data.id;
        
        // Save all media links at once for new attraction
        // Only save if there are actually media links to save
        const mediaLinksToSave = formData.media_links || [];
        console.log('💾 Saving all media links at once for new attraction...');
        console.log('💾 Total media links to save:', mediaLinksToSave.length);
        console.log('💾 Media links array:', mediaLinksToSave);
        
        // Only attempt to save if there are actually media links
        if (mediaLinksToSave.length > 0) {
          try {
            // Verify token exists before making request
            const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
            if (!token) {
              throw new Error('No authentication token found. Please log in again.');
            }
            
            // Ensure media links are properly formatted
            // IMPORTANT: Preserve ALL metadata to prevent corruption
            const formattedMediaLinks = mediaLinksToSave.map((link, index) => {
              // Validate URL is present
              const linkUrl = link.url || link.s3Url || '';
              if (!linkUrl || linkUrl.trim() === '') {
                console.error(`❌ Media link ${index} has no URL:`, link);
                throw new Error(`Media link ${index + 1} has no valid URL`);
              }
              
              // Ensure URL is absolute
              let finalUrl = linkUrl;
              if (finalUrl.startsWith('/')) {
                const apiBase = process.env.REACT_APP_API_URL || '';
                finalUrl = `${apiBase}${finalUrl}`;
              }
              
              return {
                type: link.type || 'image',
                url: finalUrl,
                filename: link.filename || null,
                isLocal: link.isLocal !== undefined ? link.isLocal : (link.filename ? true : false),
                s3Url: link.s3Url || null,
                storage: link.storage || (link.s3Url ? 'dual' : 'mount'),
                added_at: link.added_at || link.uploadedAt || new Date().toISOString(),
                // Preserve additional metadata
                originalName: link.originalName || null,
                size: link.size || null,
                mimeType: link.mimeType || null
              };
            });
            
            console.log('💾 Sending media links to backend (new attraction):', {
              count: formattedMediaLinks.length,
              attractionId: attractionId,
              hasToken: !!token,
              tokenLength: token ? token.length : 0
            });
            
            // Make the request - ensure token is in headers
            // Pass headers directly in the config object
            const response = await api.post(
              `/api/attractions/${attractionId}/save-all-media`,
              { media_links: formattedMediaLinks },
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            console.log('✅ All media saved at once for new attraction:', response.data);
          } catch (mediaError) {
            console.error('⚠️ Failed to save media links (attraction data was saved):', mediaError);
            console.error('💾 Error details:', {
              message: mediaError.message,
              response: mediaError.response?.data,
              status: mediaError.response?.status,
              statusText: mediaError.response?.statusText
            });
            // Don't throw - the main save succeeded, just log the media error
            // We'll show a warning message to the user
            mediaSaveFailed = true;
            setQuickSaveError('Attraction saved successfully, but some images may not have been saved. Please try uploading images again.');
          }
        } else {
          console.log('ℹ️ No media links to save, skipping media save step');
        }
      }

      // Refresh the attractions list
      if (onSave) {
        // Pass the correct data structure expected by parent component
        onSave({
          attractionId: attractionId,
          success: true,
          message: mediaSaveFailed ? 'Attraction saved successfully, but some images may not have been saved.' : 'Attraction and media saved successfully!'
        });
      }

      // Close the form after successful save
      // If media save failed, don't auto-close so user can see the error and try again
      if (!mediaSaveFailed) {
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        // Show alert but keep form open so user can try saving images again
        alert('Attraction data saved successfully! However, some images could not be saved. You can try uploading them again or close the form and edit the attraction later.');
      }

    } catch (error) {
      console.error('💾 Quick save error:', error);
      console.error('💾 Error response:', error.response?.data);
      console.error('💾 Error message:', error.response?.data?.message);
      console.error('💾 Full error details:', JSON.stringify(error.response?.data, null, 2));
      
      if (error.response?.data?.errors) {
        console.error('💾 Validation errors details:');
        error.response.data.errors.forEach((err, index) => {
          console.error(`💾 Error ${index + 1}:`, {
            field: err.path || 'unknown',
            message: err.msg || 'unknown',
            value: err.value || 'none',
            location: err.location || 'unknown'
          });
        });
      }
      
      // Force clear translation error again to ensure it's not set
      setTranslationError('');
      
      // Set the quick save error
      const errorMessage = error.response?.data?.message || 'Failed to save attraction. Please try again.';
      setQuickSaveError(errorMessage);
      
      console.log('💾 Set quickSaveError to:', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel translation and go back to form
  const handleCancelTranslation = () => {
    console.log('🔄 Cancelling translation preview...');
    setShowTranslationPreview(false);
    setTranslatedData(null);
    setTranslationError('');
    setIsTranslating(false);
    setIsSaving(false);
    // Keep the form open so user can continue editing
  };



  // Always render something - never return null to prevent blank pages
  if (!isOpen) {
    return null; // Only return null if modal is not open
  }

  // Show translation loading screen - ALWAYS render something, never blank
  if (isTranslating) {
    return (
      <div className="modal-overlay">
        <TranslationLoadingScreen
          currentStep={translationProgress.step || 'initializing'}
          progress={translationProgress.progress || 0}
          currentLanguage={translationProgress.currentLanguage || ''}
          completedLanguages={translationProgress.completedLanguages || 0}
          error={translationProgress.error}
          languageList={supportedCodes.filter(c => c !== 'en').map(c => getLanguageDisplay(c))}
          onCancel={() => {
            if (window.confirm('Are you sure you want to cancel the translation?')) {
              setIsTranslating(false);
              setTranslationProgress({
                step: 'initializing',
                progress: 0,
                currentLanguage: '',
                completedLanguages: 0,
                error: null
              });
            }
          }}
        />
      </div>
    );
  }

  // Show translation preview
  console.log('🔍 Render check - showTranslationPreview:', showTranslationPreview);
  console.log('🔍 Render check - translatedData:', translatedData);
  console.log('🔍 Render check - translatedData keys:', translatedData ? Object.keys(translatedData) : 'null');
  
  // Show translation preview if we have valid data
  if (showTranslationPreview && translatedData && Object.keys(translatedData).length > 0) {
    console.log('✅ Rendering TranslationPreview component');
    try {
      return (
        <div className="modal-overlay">
          <div className="modal-container translation-modal">
            <TranslationPreview
              translatedData={translatedData}
              sourceLanguage={sourceLanguage}
              supportedLanguages={languageMap}
              onApprove={handleApproveTranslations}
              onCancel={handleCancelTranslation}
              onEdit={() => {}}
              loading={isSaving}
            />
          </div>
        </div>
      );
    } catch (previewError) {
      console.error('❌ Error rendering TranslationPreview:', previewError);
      // Fallback to showing the form if preview fails
      setShowTranslationPreview(false);
      setTranslationError('Error displaying translation preview. Please try again.');
    }
  } else if (showTranslationPreview && (!translatedData || Object.keys(translatedData).length === 0)) {
    // If preview is requested but no data, show error and fallback to form
    console.log('⚠️ Translation preview requested but no valid data available');
    // Don't set state in render - use useEffect instead
    // This will be handled by the error state
  }

  // CRITICAL: Always render the form as fallback - never return blank
  // If we're not translating and not showing preview, show the form
  return (
    <div className="modal-overlay">
      <div className="modal-container enhanced-attraction-form">
        <div className="modal-header">
          <h2>
            {editingAttraction ? '✏️ Edit Attraction' : '🌟 Add New Attraction'}
          </h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="form-container">
          <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
            {/* Translation Options */}
            <div className="form-section translation-options">
              <h3>{editingAttraction ? '🌐 Multi-Language Input' : '🚀 Auto-Language Detection'}</h3>
              <div className="translation-controls">
                {editingAttraction && (
                  <div className="viewing-language-selector">
                    <label>📖 View data in language:</label>
                    <select 
                      value={viewingLanguage} 
                      onChange={(e) => setViewingLanguage(e.target.value)}
                      style={{ marginBottom: '10px' }}
                    >
                      {languages.map(lang => (
                        <option key={lang.value} value={lang.value}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {editingAttraction && (
                  <div className="source-language-selector">
                    <label>✏️ I'm writing this form in:</label>
                    <select 
                      value={sourceLanguage} 
                      onChange={(e) => setSourceLanguage(e.target.value)}
                    >
                      {languages.map(lang => (
                        <option key={lang.value} value={lang.value}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="translation-note">
                  {editingAttraction ? (
                    <>
                      ✏️ **Editing Mode:** You're currently editing in {languages.find(l => l.value === sourceLanguage)?.label || 'the selected language'}. Choose your input language above and modify the fields. When you translate, all languages will be updated.
                    </>
                  ) : (
                    <>
                      ✨ **Write in ANY language you prefer!** Our system will automatically detect your input language and create professional translations in all {supportedCodes.length} supported languages: {formatLanguageList(supportedCodes)}.
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="form-section">
              <h3>📋 Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name * <span className="language-hint">({languages.find(l => l.value === sourceLanguage)?.label})</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={errors.name ? 'error' : ''}
                    placeholder={`Enter attraction name in ${languages.find(l => l.value === sourceLanguage)?.label}`}
                    required
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label>Category * {!editingAttraction && <span style={{color: '#e74c3c', fontSize: '12px'}}>(Required for new attractions)</span>}</label>
                  {isAddingCategory ? (
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      <input
                        type="text"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        placeholder="Type a new category slug (e.g. food-trucks)"
                        className={errors.category || (!editingAttraction && !formData.category) ? 'error' : ''}
                        autoFocus
                        style={{flex: 1, ...(!editingAttraction && !formData.category ? {borderColor: '#e74c3c', backgroundColor: '#fdf2f2'} : {})}}
                      />
                      <button
                        type="button"
                        onClick={() => { setIsAddingCategory(false); setFormData(prev => ({ ...prev, category: '' })); }}
                        style={{whiteSpace: 'nowrap', padding: '8px 12px', cursor: 'pointer'}}
                      >
                        ↩ Pick from list
                      </button>
                    </div>
                  ) : (
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleCategorySelectChange}
                      className={errors.category || (!editingAttraction && !formData.category) ? 'error' : ''}
                      required
                      style={!editingAttraction && !formData.category ? {borderColor: '#e74c3c', backgroundColor: '#fdf2f2'} : {}}
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                      <option value="__add_new__">➕ Add new category…</option>
                    </select>
                  )}
                  {editingAttraction && viewingLanguage !== 'en' && formData.category && (
                    <div className="translation-display">
                      <span className="language-hint">
                        {languages.find(l => l.value === viewingLanguage)?.label}: "{getFieldValue('category')}"
                      </span>
                    </div>
                  )}
                  {errors.category && <span className="error-message">{errors.category}</span>}
                </div>

                {/* Subcategory hidden 2026-08-06 for tenants whose categories don't map
                    to the static subcategory list (e.g. VirQ's slug categories yield no
                    options). The client picks from `category`; chat retrieval buckets on
                    `category` (category_bucket), never subcategory. Rendered only when the
                    tenant actually has options, so a matching tenant keeps the control.
                    The DB column + existing values are untouched; save still round-trips
                    formData.subcategory so nothing is nulled. */}
                {getAvailableSubcategories(formData.category).length > 0 && (
                  <div className="form-group">
                    <label>Subcategory</label>
                    <select
                      name="subcategory"
                      value={formData.subcategory}
                      onChange={handleInputChange}
                      disabled={!formData.category}
                    >
                      <option value="">
                        {!formData.category ? 'Select category first' : 'Select subcategory...'}
                      </option>
                      {getAvailableSubcategories(formData.category).map(subcategory => (
                        <option key={subcategory} value={subcategory}>
                          {subcategory}
                        </option>
                      ))}
                    </select>
                    {editingAttraction && viewingLanguage !== 'en' && formData.subcategory && (
                      <div className="translation-display">
                        <span className="language-hint">
                          {languages.find(l => l.value === viewingLanguage)?.label}: "{getFieldValue('subcategory')}"
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Display Category control removed 2026-08-04: the field drove nothing a
                    visitor sees — browse tabs and chat both derive from `category`. The
                    control only misled admins (its Hotels/Events options matched zero rows).
                    The DB column and its existing values are intentionally left untouched;
                    save logic still round-trips any pre-existing value so nothing is nulled. */}

                {/* Halal Status Field - Only show when category is Restaurant AND tenant has halal_tracking enabled */}
                {halalTracking && formData.category === 'Restaurant' && (
                  <div className="form-group">
                    <label>Halal Status</label>
                    <select
                      name="halal_status"
                      value={formData.halal_status}
                      onChange={handleInputChange}
                    >
                      <option value="">Select halal status...</option>
                      <option value="halal">Halal</option>
                      <option value="non-halal">Non-Halal</option>
                      <option value="unknown">Unknown</option>
                      <option value="muslim-friendly">Muslim-Friendly</option>
                      <option value="vegetarian">Vegetarian</option>
                      <option value="vegan">Vegan</option>
                    </select>
                    <small className="form-help-text">
                      Select the halal status for this restaurant
                    </small>
                  </div>
                )}

                {/* Accommodation ratings: Star (1-5 Bintang) and Orkid (1-3) — only when category is Accommodation */}
                {formData.category === 'Accommodation' && (
                  <>
                    <div className="form-group">
                      <label>Star Rating (Bintang)</label>
                      <select
                        name="star_rating"
                        value={formData.star_rating}
                        onChange={handleInputChange}
                      >
                        <option value="">Select star rating...</option>
                        <option value="1">1 Star</option>
                        <option value="2">2 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="5">5 Stars</option>
                      </select>
                      <small className="form-help-text">
                        Rating types: 1–5 Bintang (Star)
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Orkid Rating</label>
                      <select
                        name="orkid_rating"
                        value={formData.orkid_rating}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Orkid rating...</option>
                        <option value="1">1 Orkid</option>
                        <option value="2">2 Orkid</option>
                        <option value="3">3 Orkid</option>
                      </select>
                      <small className="form-help-text">
                        Rating types: 1–3 Orkid
                      </small>
                    </div>
                  </>
                )}

                {/* Stampbook Slot Info (read-only) */}
                <div className="form-group full-width">
                  <label style={{ fontWeight: '600', marginBottom: '4px' }}>Stampbook</label>
                  {stampbookSlotInfo ? (
                    <div style={{ padding: '10px 14px', background: '#f0f4ff', border: '1px solid #d0d8f0', borderRadius: '6px', fontSize: '14px', color: '#333' }}>
                      {stampbookSlotInfo.icon || '📍'} Assigned to <strong>Slot {stampbookSlotInfo.stamp_id}</strong>
                      {stampbookSlotInfo.display_name ? ` (${stampbookSlotInfo.display_name})` : ''}
                      <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: '4px' }}>
                        Manage stampbook slots in <a href="/stampbook-configuration" style={{ color: '#667eea' }}>Stampbook Configuration</a>.
                      </span>
                    </div>
                  ) : (
                    <div style={{ padding: '10px 14px', background: '#f8f8f8', border: '1px solid #eee', borderRadius: '6px', fontSize: '13px', color: '#999' }}>
                      Not assigned to any stampbook slot.
                      <span style={{ display: 'block', marginTop: '4px' }}>
                        Assign via <a href="/stampbook-configuration" style={{ color: '#667eea' }}>Stampbook Configuration</a>.
                      </span>
                    </div>
                  )}
                </div>

              </div>

              {/* Address Section */}
              <div className="form-section">
                <h3>📍 Address</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Address 1 (optional)</label>
                    <input
                      type="text"
                      name="address_1"
                      value={formData.address_1 || ''}
                      onChange={handleInputChange}
                      placeholder="Unit, Floor Level, Building No, etc."
                    />
                    <small className="form-help-text">
                      Use only for unit/floor/building. Leave empty if not applicable. Do not repeat the street address here.
                    </small>
                  </div>

                  <div className="form-group full-width">
                    <label>Street Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address || ''}
                      onChange={handleInputChange}
                      placeholder="Street name and number, e.g. Torkkelinkatu 2"
                    />
                    <small className="form-help-text">
                      Main street address.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleInputChange}
                      placeholder="City name"
                    />
                  </div>

                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state || ''}
                      onChange={handleInputChange}
                      placeholder="State/Province (optional)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country || ''}
                      onChange={handleInputChange}
                      placeholder="Country"
                    />
                  </div>

                  <div className="form-group">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code || ''}
                      onChange={handleInputChange}
                      placeholder="Postal/ZIP code"
                    />
                  </div>

                  <div className="form-group">
                    <label>District</label>
                    <select
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                    >
                      <option value="">Select district...</option>
                      <option value="Imatrankoski">Imatrankoski</option>
                      <option value="Tainionkoski">Tainionkoski</option>
                      <option value="Mansikkala">Mansikkala</option>
                      <option value="Vuoksenniska">Vuoksenniska</option>
                      <option value="Rajapatsas">Rajapatsas</option>
                    </select>
                    <small className="form-help-text">
                      Select the district for this attraction
                    </small>
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={errors.description ? 'error' : ''}
                  placeholder="Enter a brief description"
                  rows="3"
                  required
                />
                {errors.description && <span className="error-message">{errors.description}</span>}
              </div>

              <div className="form-group full-width">
                <label>Keywords</label>
                <textarea
                  name="keywords"
                  value={formData.keywords}
                  onChange={handleInputChange}
                  placeholder="Enter keywords related to this attraction (e.g., historical, family-friendly, sunset view, photography)"
                  rows="2"
                />
                <small className="form-help-text">
                  Separate keywords with commas. These help with search and discovery.
                </small>
              </div>

            </div>

            {/* Location & Pricing */}
            <div className="form-section">
              <h3>📍 Location & Pricing</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Latitude</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    placeholder="e.g. 2.3959343"
                  />
                  <small className="form-help-text">
                    Paste full coordinates from Google Maps. Stored exactly as entered—no rounding or truncation.
                  </small>
                </div>

                <div className="form-group">
                  <label>Longitude</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    placeholder="e.g. 102.1980041"
                  />
                  <small className="form-help-text">
                    Use the full number (e.g. from Google Maps URL). Do not round or shorten.
                  </small>
                </div>

                <div className="form-group">
                  <label>Pricing Type</label>
                  <select
                    name="pricing_type"
                    value={formData.pricing_type}
                    onChange={handleInputChange}
                  >
                    {pricingTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                  >
                    {currencies.map(currency => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Adult Price</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price_adult"
                    value={formData.price_adult}
                    onChange={handleInputChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Child Price</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price_child"
                    value={formData.price_child}
                    onChange={handleInputChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Senior Citizen Price</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price_senior_citizen"
                    value={formData.price_senior_citizen}
                    onChange={handleInputChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Tourist Price</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price_tourist"
                    value={formData.price_tourist}
                    onChange={handleInputChange}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="form-section">
              <OperatingHours
                value={formData.operating_hours}
                onChange={(hours) => setFormData({ ...formData, operating_hours: hours })}
              />
            </div>

            {/* Additional Information */}
            <div className="form-section">
              <h3>📝 Additional Information</h3>
              
              <div className="form-group full-width">
                <label>Tips</label>
                <textarea
                  name="tips"
                  value={formData.tips}
                  onChange={handleInputChange}
                  placeholder="Share helpful tips for visitors"
                  rows="3"
                />
              </div>

              <div className="form-group full-width">
                <label>History</label>
                <textarea
                  name="history"
                  value={formData.history}
                  onChange={handleInputChange}
                  placeholder="Share historical information"
                  rows="3"
                />
              </div>

              <div className="form-group full-width">
                <label>Contact Person</label>
                <input
                  type="text"
                  name="pic_contact"
                  value={formData.pic_contact}
                  onChange={handleInputChange}
                  placeholder="Contact person name, title, phone"
                />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Website URL</label>
                  <input
                    type="url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Contact Number</label>
                  <input
                    type="text"
                    name="contact_number"
                    value={formData.contact_number}
                    onChange={handleInputChange}
                    placeholder="+60 12-345 6789"
                  />
                </div>

                <div className="form-group">
                  <label>Map URL</label>
                  <input
                    type="url"
                    name="map_url"
                    value={formData.map_url}
                    onChange={handleInputChange}
                    placeholder="https://maps.google.com/..."
                  />
                </div>
              </div>

              {/* Social Media Links Section */}
              <div className="form-section">
                <h3>🌐 Social Media Links</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Facebook</label>
                    <input
                      type="url"
                      value={formData.social_links.facebook || ''}
                      onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
                      placeholder="https://facebook.com/yourpage"
                    />
                  </div>

                  <div className="form-group">
                    <label>Instagram</label>
                    <input
                      type="url"
                      value={formData.social_links.instagram || ''}
                      onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
                      placeholder="https://instagram.com/youraccount"
                    />
                  </div>

                  <div className="form-group">
                    <label>Twitter / X</label>
                    <input
                      type="url"
                      value={formData.social_links.twitter || ''}
                      onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
                      placeholder="https://twitter.com/youraccount"
                    />
                  </div>

                  <div className="form-group">
                    <label>YouTube</label>
                    <input
                      type="url"
                      value={formData.social_links.youtube || ''}
                      onChange={(e) => handleSocialLinkChange('youtube', e.target.value)}
                      placeholder="https://youtube.com/@yourchannel"
                    />
                  </div>

                  <div className="form-group">
                    <label>TikTok</label>
                    <input
                      type="url"
                      value={formData.social_links.tiktok || ''}
                      onChange={(e) => handleSocialLinkChange('tiktok', e.target.value)}
                      placeholder="https://tiktok.com/@youraccount"
                    />
                  </div>

                  <div className="form-group">
                    <label>LinkedIn</label>
                    <input
                      type="url"
                      value={formData.social_links.linkedin || ''}
                      onChange={(e) => handleSocialLinkChange('linkedin', e.target.value)}
                      placeholder="https://linkedin.com/company/yourcompany"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    name="is_bookable_online"
                    checked={formData.is_bookable_online}
                    onChange={handleInputChange}
                  />
                  Available for online booking
                </label>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="form-section">
              <h3>📷 Images & Media</h3>
              
              <div className="image-upload-container">
                <div className="upload-buttons">
                  <label className="upload-btn primary">
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      disabled={isUploadingImage}
                    />
                    📁 Upload Images
                  </label>
                  
                  {isUploadingImage && <span className="uploading">Uploading...</span>}
                </div>
                
                {uploadError && (
                  <div className="upload-error">
                    ⚠️ {uploadError}
                  </div>
                )}
                
                {/* Debug Info */}
                {formData.media_links && formData.media_links.length > 0 && (
                  <div style={{background: '#f0f0f0', padding: '10px', margin: '10px 0', fontSize: '12px'}}>
                    <strong>🔍 Debug Info:</strong><br/>
                    Media Links Count: {formData.media_links.length}<br/>
                    {formData.media_links.map((media, index) => (
                      <div key={index}>
                        {index + 1}. Type: {media.type}, URL: {media.url || 'NO URL'}
                      </div>
                    ))}
                  </div>
                )}

                {/* Image Preview Grid */}
                {formData.media_links && formData.media_links.length > 0 && (
                  <div className="media-preview-grid">
                    {formData.media_links.map((media, index) => (
                      <div key={index} className="media-preview-item">
                        {media.type === 'image' ? (
                          <img 
                            src={media.url} 
                            alt={media.caption || `Image ${index + 1}`}
                            className="preview-image"
                            onError={(e) => {
                              console.error('❌ Image load error for:', media.url);
                              console.error('Error event:', e);
                              console.error('Current page URL:', window.location.href);
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                            onLoad={(e) => {
                              console.log('✅ Image loaded successfully:', media.url);
                              console.log('Image dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
                            }}
                            style={{
                              border: '2px solid #ddd',
                              borderRadius: '4px'
                            }}
                          />
                        ) : null}
                        <div 
                          className="media-placeholder" 
                          style={{ display: media.type === 'image' ? 'none' : 'flex' }}
                        >
                          {media.type === 'image' ? (
                            <>
                              🖼️ Image<br/>
                              <small style={{fontSize: '10px', wordBreak: 'break-all'}}>
                                {media.url}
                              </small>
                            </>
                          ) : (
                            `🎥 ${media.type}`
                          )}
                        </div>
                        <div className="media-overlay">
                          <button
                            type="button"
                            className="remove-media-btn"
                            onClick={() => removeImage(media)}
                            title="Remove"
                          >
                            ❌
                          </button>
                        </div>
                        {media.caption && (
                          <div className="media-caption">{media.caption}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* URL Input removed per user request */}
              </div>
            </div>

            {/* Error Display */}
            {translationError && (
              <div className="error-section">
                <h4>Translation Error</h4>
                <p>{translationError}</p>
                <button 
                  type="button" 
                  onClick={() => setTranslationError('')}
                  className="btn-dismiss"
                >
                  Dismiss
                </button>
              </div>
            )}
            
            {/* Quick Save Error Display */}
            {quickSaveError && (
              <div className="error-section">
                <h4>Save Error</h4>
                <p>{quickSaveError}</p>
                <button 
                  type="button" 
                  onClick={() => setQuickSaveError('')}
                  className="btn-dismiss"
                >
                  Dismiss
                </button>
              </div>
            )}
            
            {/* Debug Error States */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{background: '#f0f0f0', padding: '10px', margin: '10px 0', fontSize: '12px'}}>
                <strong>Debug Error States:</strong><br/>
                translationError: "{translationError}"<br/>
                quickSaveError: "{quickSaveError}"<br/>
                errors: {JSON.stringify(errors)}
              </div>
            )}

            {/* Action Buttons */}
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              
              <button 
                type="button" 
                className="btn-save-secondary"
                onClick={handleQuickSave}
                disabled={isSubmitting || isTranslating}
                style={{
                  background: 'linear-gradient(135deg, #28a745, #20c997)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSubmitting || isTranslating ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting || isTranslating ? 0.6 : 1,
                  transition: 'all 0.3s ease',
                  marginRight: '10px'
                }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    💾 Save Image & Data Only
                  </>
                )}
              </button>
              
              <button
                type="button"
                className="btn-translate-primary"
                onClick={handleTranslationWorkflow}
                disabled={isSubmitting || isTranslating}
              >
                {isTranslating ? (
                  <>
                    <span className="spinner"></span>
                    Translating...
                  </>
                ) : (
                  <>
                    🌐 Translate & Save ({formatLanguageList(supportedCodes)})
                  </>
                )}
              </button>

              {editingAttraction && supportedCodes.length > 1 && (
                <button
                  type="button"
                  className="btn-translate-primary"
                  style={{ background: '#6b7280' }}
                  onClick={handleReviewTranslations}
                  disabled={isSubmitting || isTranslating}
                >
                  📝 Review / Edit Translations
                </button>
              )}
            </div>

            <div className="workflow-info">
              <p>💾 <strong>Save Image & Data Only:</strong> Quick save without translation (English only)</p>
              {editingAttraction ? (
                <p>✨ <strong>Translate & Save:</strong> Update all {supportedCodes.length} languages based on your selected input language, then review and approve!</p>
              ) : (
                <p>✨ <strong>Translate & Save:</strong> Auto-detect your input language and create translations in all {supportedCodes.length} languages, then review and approve!</p>
              )}
              {editingAttraction && supportedCodes.length > 1 && (
                <p>📝 <strong>Review / Edit Translations:</strong> Open existing translations for review and hand-editing without re-translating.</p>
              )}
            </div>
          </form>
        </div>
      </div>
      
    </div>
  );
};

export default EnhancedAttractionForm;